import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import {
  ColumnDefinition,
  TableAction,
} from '@shared/components/master-table/master-table.component';
import { MatTableDataSource } from '@angular/material/table';
import {
  JourneyConfig,
  JourneyCreatePayload,
  JourneyFormValue,
  JourneyUpdatePayload,
} from './journey.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';

type JourneyMode = 'view' | 'create' | 'edit';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-journey-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './journey-detail.component.html',
  styleUrl: './journey-detail.component.scss',
})
export class JourneyDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: JourneyMode = 'view';
  selectedJourneyId: string | null = null;
  journeys: JourneyConfig[] = [];
  companies: CompaniesRecord[] = [];
  selectedCompanyId = '';
  dataSource = new MatTableDataSource<JourneyConfig>([]);
  selectedJourney: JourneyConfig | null = null;
  form: JourneyFormValue = this.createEmptyForm();
  actions: TableAction<JourneyConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];

  readonly readAccess = { resource: 'journeys', action: 'read' } as const;
  readonly createAccess = { resource: 'journeys', action: 'create' } as const;
  readonly updateAccess = { resource: 'journeys', action: 'update' } as const;
  readonly deleteAccess = { resource: 'journeys', action: 'delete' } as const;
  readonly journeyTypes = ['daytime', 'nighttime', 'mixed'] as const;

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.journeys = this.journeys.map((journey) => this.normalizeJourney(journey));
        this.dataSource.data = this.journeys;
        if (this.selectedJourney) {
          this.selectedJourney = this.normalizeJourney(this.selectedJourney);
        }
        this.configureTable();
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });
    this.configureTable();
    this.dataSource.filterPredicate = (journey, filter) =>
      [
        journey.name,
        journey.workMinutesLabel,
        journey.mealBreakLabel,
        journey.journeyTypeLabel,
        journey.activeLabel,
      ]
        .join(' ')
        .toLowerCase()
        .includes(filter);
    void this.loadCompanies(false).then(() => this.syncWithRoute());
  }

  get hasJourneys(): boolean {
    return this.journeys.length > 0;
  }

  get hasMultipleCompanies(): boolean {
    return this.companies.length > 1;
  }

  get canCreate(): boolean {
    return this.hasAccess('create');
  }

  get canUpdate(): boolean {
    return this.hasAccess('update');
  }

  get canDelete(): boolean {
    return this.hasAccess('delete');
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('journeysCatalog.detail.createTitle');
    }

    if (this.isEditMode) {
      return this.t('journeysCatalog.detail.editTitle');
    }

    return this.selectedJourney?.name || this.t('journeysCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('journeysCatalog.detail.createDescription');
    }

    if (this.isEditMode) {
      return this.t('journeysCatalog.detail.editDescription');
    }

    return this.t('journeysCatalog.detail.description');
  }

  get journeyMealMinutes(): number {
    if (!this.selectedJourney?.includesMealBreak) {
      return 0;
    }
    return Math.max(0, Number(this.selectedJourney.mealBreakMinutes || 0));
  }

  get journeyTotalMinutes(): number {
    return Math.max(0, Number(this.selectedJourney?.workMinutes || 0)) + this.journeyMealMinutes;
  }

  get journeyWorkSegmentPercent(): number {
    if (!this.journeyTotalMinutes) {
      return 50;
    }
    return (Number(this.selectedJourney?.workMinutes || 0) / 2 / this.journeyTotalMinutes) * 100;
  }

  get journeyMealSegmentPercent(): number {
    if (!this.journeyTotalMinutes) {
      return 0;
    }
    return (this.journeyMealMinutes / this.journeyTotalMinutes) * 100;
  }

  get journeyTypeIcon(): string {
    switch (`${this.selectedJourney?.journeyType || ''}`.toLowerCase()) {
      case 'nighttime': return 'dark_mode';
      case 'mixed': return 'brightness_6';
      default: return 'light_mode';
    }
  }

  get activeJourneysCount(): number {
    return this.journeys.filter((journey) => journey.isActive).length;
  }

  get journeysWithMealBreak(): number {
    return this.journeys.filter((journey) => journey.includesMealBreak).length;
  }

  get averageWorkMinutes(): number {
    if (!this.journeys.length) {
      return 0;
    }

    const total = this.journeys.reduce((sum, journey) => sum + journey.workMinutes, 0);
    return Math.round(total / this.journeys.length);
  }

  get formPreviewWorkMinutes(): number {
    return Number(this.form.workMinutes || 0);
  }

  get formPreviewMealMinutes(): number {
    return this.form.includesMealBreak ? Number(this.form.mealBreakMinutes || 0) : 0;
  }

  get formPreviewTotalMinutes(): number {
    return this.formPreviewWorkMinutes + this.formPreviewMealMinutes;
  }

  get workDurationHours(): number | '' {
    return this.getDurationHours(this.form.workMinutes);
  }

  get workDurationMinutes(): number | '' {
    return this.getDurationMinutePart(this.form.workMinutes);
  }

  get mealDurationHours(): number | '' {
    return this.getDurationHours(this.form.mealBreakMinutes);
  }

  get mealDurationMinutes(): number | '' {
    return this.getDurationMinutePart(this.form.mealBreakMinutes);
  }

  async loadJourneys(
    preferredJourneyId: string | null = this.selectedJourneyId,
    navigateSelection = true
  ): Promise<void> {
    if (!this.selectedCompanyId) {
      this.journeys = [];
      this.dataSource.data = [];
      this.selectedJourneyId = null;
      this.selectedJourney = null;
      this.mode = 'view';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<JourneyConfig[]>('labor-config/journeys', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.journeys = (response || []).map((journey) => this.normalizeJourney(journey));
      this.dataSource.data = this.journeys;
      const requestedJourney = preferredJourneyId
        ? this.journeys.find((journey) => journey.journeyId === preferredJourneyId) || null
        : null;

      // The route id is authoritative. A journey can belong to a company other
      // than the initially selected one, so do not silently replace it with the
      // first result from the company-filtered catalog.
      if (requestedJourney) {
        await this.selectJourney(requestedJourney.journeyId, false, navigateSelection);
      } else if (preferredJourneyId) {
        await this.selectJourney(preferredJourneyId, false, navigateSelection);
        await this.reloadJourneysForSelectedCompany(preferredJourneyId);
      } else if (this.journeys[0]) {
        await this.selectJourney(this.journeys[0].journeyId, false, navigateSelection);
      } else {
        this.selectedJourneyId = null;
        this.selectedJourney = null;
        this.mode = 'view';
      }
    } catch (error) {
      console.error('Error loading journeys', error);
      this.journeys = [];
      this.dataSource.data = [];
      this.selectedJourneyId = null;
      this.selectedJourney = null;
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('journeysCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private async reloadJourneysForSelectedCompany(selectedJourneyId: string): Promise<void> {
    if (!this.selectedCompanyId) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<JourneyConfig[]>('labor-config/journeys', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.journeys = (response || []).map((journey) => this.normalizeJourney(journey));
      this.dataSource.data = this.journeys;

      const selectedFromCatalog = this.journeys.find(
        (journey) => journey.journeyId === selectedJourneyId
      );
      if (selectedFromCatalog) {
        this.selectedJourney = selectedFromCatalog;
        this.form = this.createFormFromJourney(selectedFromCatalog);
      }
    } catch (error) {
      // Keep the detail already loaded by id. A catalog refresh failure must not
      // replace the requested journey or prevent it from being edited.
      console.error('Error syncing journeys for selected company', error);
    }
  }

  async selectJourney(
    journeyId: string,
    showLoader = true,
    navigate = true
  ): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }

    if (showLoader) {
      this.isLoading = true;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<JourneyConfig>(`labor-config/journeys/${journeyId}`)
      );
      const normalizedJourney = this.normalizeJourney(response);

      this.selectedJourneyId = normalizedJourney.journeyId;
      this.selectedJourney = normalizedJourney;
      this.selectedCompanyId = normalizedJourney.companiesRecordId || this.selectedCompanyId;
      this.mode = 'view';
      this.form = this.createFormFromJourney(normalizedJourney);
      if (navigate) {
        void this.router.navigate(['/attendance/journeys', normalizedJourney.journeyId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
    } catch (error) {
      console.error('Error loading journey detail', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('journeysCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
    } finally {
      if (showLoader) {
        this.isLoading = false;
      }
    }
  }

  async refresh(): Promise<void> {
    await this.syncWithRoute();
  }

  onSearchChange(search: string): void {
    this.dataSource.filter = search.trim().toLowerCase();
  }

  async onCompanyChange(): Promise<void> {
    this.selectedCompanyId = this.form.companiesRecordId;
  }

  onRowClick(journey: JourneyConfig): void {
    void this.router.navigate(['/attendance/journeys', journey.journeyId]);
  }

  onEditRow(journey: JourneyConfig): void {
    if (!this.canUpdate) {
      return;
    }

    void this.router.navigate(['/attendance/journeys', journey.journeyId, 'edit'], {
      queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
    });
  }

  onDeleteRow(journey: JourneyConfig): void {
    void this.selectJourney(journey.journeyId, false, false).then(() => this.deleteSelected());
  }

  startCreate(): void {
    this.enterCreateMode(true);
  }

  private enterCreateMode(navigate = false): void {
    if (!this.canCreate) {
      return;
    }

    this.mode = 'create';
    this.selectedJourneyId = null;
    this.selectedJourney = null;
    this.selectedCompanyId = this.getDefaultCompanyId();
    this.form = this.createEmptyForm();

    if (navigate) {
      void this.router.navigate(['/attendance/journeys/new'], {
        queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
      });
    }
  }

  startEdit(): void {
    this.enterEditMode(true);
  }

  private enterEditMode(navigate = false): void {
    if (!this.selectedJourney || !this.canUpdate) {
      return;
    }

    this.mode = 'edit';
    this.form = this.createFormFromJourney(this.selectedJourney);

    if (navigate && this.selectedJourneyId) {
      void this.router.navigate(['/attendance/journeys', this.selectedJourneyId, 'edit'], {
        queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
      });
    }
  }

  cancelEdit(): void {
    this.exitEditMode(true);
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/journeys']);
  }

  private exitEditMode(navigate = false): void {
    if (this.selectedJourney) {
      this.mode = 'view';
      this.form = this.createFormFromJourney(this.selectedJourney);
      if (navigate && this.selectedJourneyId) {
        void this.router.navigate(['/attendance/journeys', this.selectedJourneyId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
      return;
    }

    if (this.journeys.length) {
      const firstJourneyId = this.journeys[0].journeyId;
      if (navigate) {
        void this.router.navigate(['/attendance/journeys', firstJourneyId]);
      } else {
        void this.selectJourney(firstJourneyId, false, false);
      }
      return;
    }

    this.mode = 'view';
    this.form = this.createEmptyForm();

    if (navigate) {
      void this.router.navigate(['/attendance/journeys']);
    }
  }

  private async syncWithRoute(): Promise<void> {
    const journeyId = this.route.snapshot.paramMap.get('journeyId');
    const companyId = this.route.snapshot.queryParamMap.get('companyId') || '';
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/journeys/new');
    const isEditRoute = /\/attendance\/journeys\/[^/]+\/edit$/.test(currentUrl);

    if (
      companyId &&
      this.companies.some((company) => company.companiesRecordId === companyId) &&
      companyId !== this.selectedCompanyId
    ) {
      this.selectedCompanyId = companyId;
    }

    await this.loadJourneys(journeyId, false);

    if (isCreateRoute) {
      this.enterCreateMode(false);
      return;
    }

    if (journeyId && isEditRoute) {
      this.enterEditMode(false);
    }
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const createdJourney = await firstValueFrom(
          this.apiService.post<JourneyConfig>('labor-config/journeys', payload)
        );
        const createdJourneyId = createdJourney?.journeyId || null;
        await this.loadJourneys(createdJourneyId, false);
        this.showNotification(
          'snackbar-success',
          this.t('journeysCatalog.notifications.created'),
          'bottom',
          'center'
        );
        if (createdJourneyId) {
          void this.router.navigate(['/attendance/journeys', createdJourneyId]);
        }
      } else if (this.isEditMode && this.selectedJourneyId) {
        const payload = this.buildUpdatePayload();
        await firstValueFrom(
          this.apiService.patch<JourneyConfig>(
            `labor-config/journeys/${this.selectedJourneyId}`,
            payload
          )
        );
        await this.loadJourneys(this.selectedJourneyId, false);
        this.showNotification(
          'snackbar-success',
          this.t('journeysCatalog.notifications.updated'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/journeys', this.selectedJourneyId]);
      }
    } catch (error) {
      console.error('Error saving journey', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('journeysCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedJourney || !this.canDelete) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('journeysCatalog.dialog.deleteTitle'),
        message: this.t('journeysCatalog.dialog.deleteMessage', {
          journey: this.selectedJourney.name,
        }),
        confirmLabel: this.t('journeysCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedJourneyId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(
          this.apiService.delete(`labor-config/journeys/${this.selectedJourneyId}`)
        );
        await this.loadJourneys(null, false);
        this.showNotification(
          'snackbar-success',
          this.t('journeysCatalog.notifications.deleted'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/journeys']);
      } catch (error) {
        console.error('Error deleting journey', error);
        this.showNotification(
          'snackbar-danger',
          this.getApiErrorMessage(error, this.t('journeysCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  onMealBreakToggle(): void {
    if (!this.form.includesMealBreak) {
      this.form.mealBreakMinutes = '';
    }
  }

  onWorkHoursChange(value: unknown): void {
    this.form.workMinutes = this.composeDuration(value, this.workDurationMinutes);
  }

  onWorkMinutesChange(value: unknown): void {
    this.form.workMinutes = this.composeDuration(this.workDurationHours, value);
  }

  onMealHoursChange(value: unknown): void {
    this.form.mealBreakMinutes = this.composeDuration(value, this.mealDurationMinutes);
  }

  onMealMinutesChange(value: unknown): void {
    this.form.mealBreakMinutes = this.composeDuration(this.mealDurationHours, value);
  }

  setWorkDuration(totalMinutes: number): void {
    this.form.workMinutes = totalMinutes;
  }

  setMealDuration(totalMinutes: number): void {
    this.form.mealBreakMinutes = totalMinutes;
  }

  isWorkDurationSelected(totalMinutes: number): boolean {
    return Number(this.form.workMinutes || 0) === totalMinutes;
  }

  isMealDurationSelected(totalMinutes: number): boolean {
    return Number(this.form.mealBreakMinutes || 0) === totalMinutes;
  }

  getWorkMinutesLabel(value: number): string {
    if (!value) {
      return this.t('journeysCatalog.labels.noWorkMinutes');
    }

    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    if (!minutes) {
      return this.t('journeysCatalog.labels.hoursExact', { hours });
    }

    return this.t('journeysCatalog.labels.hoursMinutes', { hours, minutes });
  }

  getMealBreakLabel(journey: JourneyConfig | JourneyFormValue): string {
    if (!journey.includesMealBreak) {
      return this.t('journeysCatalog.labels.withoutMealBreak');
    }

    const minutes = Number(journey.mealBreakMinutes || 0);
    return this.t('journeysCatalog.labels.mealBreakMinutes', { minutes });
  }

  getJourneyTypeLabel(value: string | null | undefined): string {
    const normalizedValue = this.normalizeOptionalText(value);
    if (!normalizedValue) {
      return this.t('journeysCatalog.labels.freeType');
    }

    if (this.journeyTypes.includes(normalizedValue as (typeof this.journeyTypes)[number])) {
      return this.t(`journeysCatalog.journeyTypeOptions.${normalizedValue}`);
    }

    return normalizedValue;
  }

  getCompanyName(companiesRecordId: string | null | undefined): string {
    if (!companiesRecordId) {
      return '-';
    }

    return (
      this.companies.find((company) => company.companiesRecordId === companiesRecordId)?.name || '-'
    );
  }

  private getDurationHours(value: number | ''): number | '' {
    if (value === '' || value === null || value === undefined) {
      return '';
    }

    return Math.floor(Math.max(0, Number(value) || 0) / 60);
  }

  private getDurationMinutePart(value: number | ''): number | '' {
    if (value === '' || value === null || value === undefined) {
      return '';
    }

    return Math.max(0, Number(value) || 0) % 60;
  }

  private composeDuration(hoursValue: unknown, minutesValue: unknown): number | '' {
    const hasHours = hoursValue !== '' && hoursValue !== null && hoursValue !== undefined;
    const hasMinutes = minutesValue !== '' && minutesValue !== null && minutesValue !== undefined;
    if (!hasHours && !hasMinutes) {
      return '';
    }

    const hours = Math.max(0, Math.floor(Number(hoursValue) || 0));
    const minutes = Math.min(59, Math.max(0, Math.floor(Number(minutesValue) || 0)));
    const totalMinutes = (hours * 60) + minutes;
    return totalMinutes > 0 ? totalMinutes : '';
  }

  private createEmptyForm(): JourneyFormValue {
    return {
      companiesRecordId: this.getDefaultCompanyId(),
      name: '',
      workMinutes: '',
      includesMealBreak: false,
      mealBreakMinutes: '',
      journeyType: '',
    };
  }

  private createFormFromJourney(journey: JourneyConfig): JourneyFormValue {
    return {
      companiesRecordId: journey.companiesRecordId || this.selectedCompanyId,
      name: journey.name,
      workMinutes: journey.workMinutes,
      includesMealBreak: journey.includesMealBreak,
      mealBreakMinutes: journey.includesMealBreak ? journey.mealBreakMinutes : '',
      journeyType: journey.journeyType || '',
    };
  }

  private validateForm(): string | null {
    if (!this.form.companiesRecordId) {
      return this.t('orgUnitsCatalog.validationCompany');
    }

    const name = this.normalizeOptionalText(this.form.name);
    const workMinutes = Number(this.form.workMinutes);
    const mealBreakMinutes = Number(this.form.mealBreakMinutes || 0);

    if (!name) {
      return this.t('journeysCatalog.notifications.validationName');
    }

    if (!Number.isFinite(workMinutes) || workMinutes <= 0) {
      return this.t('journeysCatalog.notifications.validationWorkMinutes');
    }

    if (!this.form.includesMealBreak && mealBreakMinutes > 0) {
      return this.t('journeysCatalog.notifications.validationMealBreakExcluded');
    }

    if (this.form.includesMealBreak && mealBreakMinutes <= 0) {
      return this.t('journeysCatalog.notifications.validationMealBreakRequired');
    }

    return null;
  }

  private buildCreatePayload(): JourneyCreatePayload {
    const payload: JourneyCreatePayload = {
      companiesRecordId: this.form.companiesRecordId,
      name: this.normalizeOptionalText(this.form.name) || '',
      workMinutes: Number(this.form.workMinutes),
      includesMealBreak: this.form.includesMealBreak,
    };

    if (this.form.includesMealBreak) {
      payload.mealBreakMinutes = Number(this.form.mealBreakMinutes);
    }

    const journeyType = this.normalizeOptionalText(this.form.journeyType);
    if (journeyType) {
      payload.journeyType = journeyType;
    }

    return payload;
  }

  private buildUpdatePayload(): JourneyUpdatePayload {
    return {
      companiesRecordId: this.form.companiesRecordId,
      name: this.normalizeOptionalText(this.form.name) || '',
      workMinutes: Number(this.form.workMinutes),
      includesMealBreak: this.form.includesMealBreak,
      mealBreakMinutes: this.form.includesMealBreak ? Number(this.form.mealBreakMinutes) : 0,
      journeyType: this.normalizeOptionalText(this.form.journeyType),
    };
  }

  private normalizeJourney(journey: Partial<JourneyConfig>): JourneyConfig {
    const includesMealBreak = !!journey.includesMealBreak;
    const workMinutes = Number(journey.workMinutes || 0);
    const mealBreakMinutes = Number(journey.mealBreakMinutes || 0);
    const journeyType = this.normalizeOptionalText(journey.journeyType) || null;
    const isActive = journey.isActive !== false;

    return {
      journeyId: journey.journeyId || crypto.randomUUID(),
      center: journey.center || '',
      companiesRecordId: journey.companiesRecordId,
      name: journey.name || '',
      workMinutes,
      includesMealBreak,
      mealBreakMinutes,
      journeyType,
      isActive,
      workMinutesLabel: this.getWorkMinutesLabel(workMinutes),
      mealBreakLabel: includesMealBreak
        ? this.t('journeysCatalog.labels.mealBreakMinutes', { minutes: mealBreakMinutes })
        : this.t('journeysCatalog.labels.withoutMealBreak'),
      journeyTypeLabel: journeyType || this.t('journeysCatalog.labels.freeType'),
      activeLabel: isActive
        ? this.t('common.status.active')
        : this.t('common.status.inactive'),
    };
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const normalizedValue = value.trim();
    return normalizedValue || undefined;
  }

  private async loadCompanies(preserveSelection = true): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.companyDirectory.getAllActive();

      this.companies = this.normalizeCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));

      if (
        !preserveSelection ||
        !this.selectedCompanyId ||
        !this.companies.some((company) => company.companiesRecordId === this.selectedCompanyId)
      ) {
        this.selectedCompanyId = this.companies[0]?.companiesRecordId || '';
      }
    } catch (error) {
      console.error('Error loading companies for journeys', error);
      this.companies = [];
      this.selectedCompanyId = '';
    }
  }

  private getDefaultCompanyId(): string {
    if (this.companies.length === 1) {
      return this.selectedCompanyId || this.companies[0]?.companiesRecordId || '';
    }

    return '';
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    return extractApiErrorMessage(error, fallback);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'journeys', action });
  }

  private configureTable(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('journeysCatalog.actions.create'),
        colorClass: 'success',
        auth: this.createAccess,
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: this.readAccess,
        behavior: 'refresh',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: this.updateAccess,
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: this.deleteAccess,
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('journeysCatalog.fields.name'), type: 'text', visible: true },
      {
        def: 'workMinutesLabel',
        label: this.t('journeysCatalog.fields.workMinutes'),
        type: 'text',
        visible: true,
      },
      {
        def: 'mealBreakLabel',
        label: this.t('journeysCatalog.fields.includesMealBreak'),
        type: 'text',
        visible: true,
      },
      {
        def: 'journeyTypeLabel',
        label: this.t('journeysCatalog.fields.journeyType'),
        type: 'text',
        visible: true,
      },
      {
        def: 'activeLabel',
        label: this.t('journeysCatalog.fields.status'),
        type: 'status',
        visible: true,
        statusBadgeMap: {
          [this.t('common.status.active')]: 'badge badge-solid-green',
          [this.t('common.status.inactive')]: 'badge badge-solid-red',
        },
      },
    ];
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
